/**
 * WorkflowTab — pipeline-step editor (WU-3 / R-93, 2026-05-21).
 *
 * Restores the step-editing surface removed in WU-6.3 (the rules editor
 * correctly moved to Form Config → Rules; the pipeline-step controls
 * were removed alongside it and never came back). This tab edits the
 * job type's PipelineStep rows: add/remove from the catalog, drag to
 * reorder, rename, and set a per-step routing condition (the runtime
 * engine's autoAdvanceCondition — when met, the doc skips that step).
 *
 * Convention: one step per stepType. The DB does not enforce this
 * (no (jobTypeId, stepType) unique) but the Add menu hides already-
 * present types so the UI never creates a duplicate.
 */
import "./WorkflowTab.css";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "@/components/Toaster";
import { getApiErrorMessage } from "@/lib/error-toast";
import { formatStep } from "@/lib/formatters";
import { usePipelineSteps } from "../hooks/usePipelineSteps";
import type { PipelineStepDTO } from "../hooks/useJobTypes";
import {
  RoutingConditionSummary,
  type AutoAdvanceCondition,
} from "./RoutingConditionSummary";
import { RoutingConditionDrawer } from "./RoutingConditionDrawer";
import type { Surface } from "@capture/shared";

// Mirrors the backend STEP_TYPES enum in
// apps/api/src/routes/job-types/pipeline-steps.ts — keep in sync if the
// API adds a step type. The POST route validates against that enum.
const STEP_CATALOG = [
  "scan", "classify", "key", "verify", "qc",
  "supervisor", "escalation", "export", "audit", "custom",
] as const;
// SP3 §2 — offered ONLY on remittance-surface jobs (the API enforces; this
// keeps the picker honest). Combined XOR split — the API's shape validation
// rejects mixing, surfaced via the mutation error toast.
const REMITTANCE_STEP_CATALOG = ["remittance", "remittance-key", "remittance-balance"] as const;

// F-006 — escalation is a SIDE-CHANNEL target: documents are routed there when
// they escalate, not passed through after Export. Rendering it as a trailing
// linear step made the pipeline read as "Export isn't last." These step types
// render in a separate, labeled side-channel section instead of the linear,
// drag-sortable happy-path list.
const SIDE_CHANNEL_STEP_TYPES = new Set<string>(["escalation"]);

// F-046 — step types where the verification station runs and the per-step
// re-verification policy therefore applies. scan/classify/export have no
// keying surface, so no policy control renders there.
const REVERIFY_CAPABLE_STEP_TYPES = new Set<string>([
  "key", "verify", "qc", "supervisor", "audit", "custom", "escalation", "remittance-key",
]);

interface WorkflowTabProps {
  jobTypeId: string;
  fields: Array<{ internalName: string; label: string }>;
  /** SP3 §2 — gates whether REMITTANCE_STEP_CATALOG is offered in the Add menu. */
  surface?: Surface;
}

/**
 * Closes a popup menu on Escape or on a pointerdown outside `ref`.
 * `ref` should wrap the trigger + menu so clicking the trigger to
 * toggle does not immediately re-close it.
 */
function useMenuDismiss(
  ref: React.RefObject<HTMLElement | null>,
  isOpen: boolean,
  onClose: () => void,
) {
  useEffect(() => {
    if (!isOpen) return;
    function onPointerDown(e: PointerEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [ref, isOpen, onClose]);
}

interface StepRowActions {
  onRemove: () => void;
  onRename: () => void;
  onEditCondition: () => void;
  /** F-046 — persist a re-check policy change ("standard" clears to null). */
  onChangeReverifyPolicy: (value: string) => void;
}

/**
 * The label + condition + ⋮-menu portion of a step row, shared by the
 * sortable linear rows and the static side-channel rows. `dragHandle` is
 * supplied only by the sortable wrapper.
 */
function StepRowContent({
  step,
  onRemove,
  onRename,
  onEditCondition,
  onChangeReverifyPolicy,
  dragHandle,
}: { step: PipelineStepDTO; dragHandle?: React.ReactNode } & StepRowActions) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useMenuDismiss(
    menuRef,
    menuOpen,
    useCallback(() => setMenuOpen(false), []),
  );
  return (
    <>
      {dragHandle}
      <span className="workflow-step-row__label">{step.label}</span>
      {/* F-046 — per-step re-verification policy. Standard = today's
          confidence-driven flagging (hand-keyed fields re-flag at every
          later step); the narrowed options make a second-touch step's
          "N need attention" honest. Failing rules and empty required
          fields always flag regardless. */}
      {REVERIFY_CAPABLE_STEP_TYPES.has(step.stepType) && (
        <select
          className="workflow-step-row__reverify"
          aria-label={`Re-check policy for ${step.label}`}
          title="Which fields re-flag for attention when a document arrives at this step"
          value={step.reverifyPolicy ?? "standard"}
          onChange={(e) => onChangeReverifyPolicy(e.target.value)}
        >
          <option value="standard">Re-check: standard</option>
          <option value="only_failures">Re-check: only failures</option>
          <option value="only_unverified">Re-check: only unverified</option>
        </select>
      )}
      <button
        type="button"
        className="workflow-step-row__condition"
        aria-label={`Edit condition for ${step.label}`}
        onClick={onEditCondition}
      >
        <RoutingConditionSummary condition={step.autoAdvanceCondition} />
      </button>
      <div className="workflow-step-row__menu" ref={menuRef}>
        <button
          type="button"
          aria-label={`Row menu for ${step.label}`}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
        >
          ⋮
        </button>
        {menuOpen && (
          <div role="menu" className="workflow-step-row__menu-list">
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                onRename();
              }}
            >
              Rename
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                onRemove();
              }}
            >
              Remove
            </button>
          </div>
        )}
      </div>
    </>
  );
}

/** Sortable linear step row (the happy-path sequence). */
function StepRow({ step, ...actions }: { step: PipelineStepDTO } & StepRowActions) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: step.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div ref={setNodeRef} style={style} className="workflow-step-row">
      <StepRowContent
        step={step}
        {...actions}
        dragHandle={
          <button
            type="button"
            className="workflow-step-row__handle"
            aria-label={`Drag to reorder ${step.label}`}
            {...attributes}
            {...listeners}
          >
            ⠿
          </button>
        }
      />
    </div>
  );
}

/**
 * F-006 — static (non-sortable) side-channel step row. No drag handle: a
 * side-channel isn't positional in the linear sequence.
 */
function SideChannelStepRow({ step, ...actions }: { step: PipelineStepDTO } & StepRowActions) {
  return (
    <div className="workflow-step-row workflow-step-row--side-channel">
      <StepRowContent step={step} {...actions} />
    </div>
  );
}

export function WorkflowTab({ jobTypeId, fields, surface }: WorkflowTabProps) {
  const { steps, isLoading, error, addStep, removeStep, updateStep, reorder } =
    usePipelineSteps(jobTypeId);
  const sensors = useSensors(useSensor(PointerSensor));
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<PipelineStepDTO | null>(null);
  const addMenuRef = useRef<HTMLDivElement>(null);
  useMenuDismiss(
    addMenuRef,
    addMenuOpen,
    useCallback(() => setAddMenuOpen(false), []),
  );

  const presentTypes = new Set(steps.map((s) => s.stepType));
  // SP3 §2 — remittance step types are only offered on remittance-surface
  // jobs; the API enforces regardless, this just keeps the picker honest.
  const catalog: readonly string[] =
    surface === "remittance" ? [...STEP_CATALOG, ...REMITTANCE_STEP_CATALOG] : STEP_CATALOG;
  const addable = catalog.filter((t) => !presentTypes.has(t));

  // F-006 — split the linear happy-path from side-channel targets (escalation)
  // so Export reads as the terminal step and the side-channel is visually
  // distinct from the ordered sequence.
  const linearSteps = steps.filter((s) => !SIDE_CHANNEL_STEP_TYPES.has(s.stepType));
  const sideChannelSteps = steps.filter((s) => SIDE_CHANNEL_STEP_TYPES.has(s.stepType));

  async function handleAdd(stepType: string) {
    setAddMenuOpen(false);
    try {
      await addStep(stepType);
    } catch (e) {
      toast.error(getApiErrorMessage(e, { fallback: "Couldn't add step" }));
    }
  }

  async function handleRemove(step: PipelineStepDTO) {
    try {
      await removeStep(step.id);
    } catch (e) {
      toast.error(getApiErrorMessage(e, { fallback: "Couldn't remove step" }));
    }
  }

  async function handleRename(step: PipelineStepDTO) {
    const next = window.prompt("Step label", step.label);
    if (next == null || next.trim() === "" || next === step.label) return;
    try {
      await updateStep(step.id, { label: next.trim() });
    } catch (e) {
      toast.error(getApiErrorMessage(e, { fallback: "Couldn't rename step" }));
    }
  }

  async function handleDragEnd(e: DragEndEvent) {
    if (!e.over || e.active.id === e.over.id) return;
    // Only the linear steps are sortable; reorder within that subset, then
    // keep the side-channel steps (escalation) appended so they retain a
    // stable trailing order without being part of the draggable sequence.
    const oldIndex = linearSteps.findIndex((s) => s.id === e.active.id);
    const newIndex = linearSteps.findIndex((s) => s.id === e.over!.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const orderedLinear = arrayMove(linearSteps, oldIndex, newIndex).map((s) => s.id);
    const ordered = [...orderedLinear, ...sideChannelSteps.map((s) => s.id)];
    try {
      await reorder(ordered);
    } catch {
      toast.error("Couldn't save the new step order — re-checking server state");
    }
  }

  // F-046 — persist the re-check policy; "standard" clears the column so the
  // step falls back to the default behavior explicitly.
  async function handleChangeReverifyPolicy(step: PipelineStepDTO, value: string) {
    try {
      await updateStep(step.id, { reverifyPolicy: value === "standard" ? null : value });
    } catch (e) {
      toast.error(getApiErrorMessage(e, { fallback: "Couldn't save re-check policy" }));
    }
  }

  async function handleSaveCondition(
    condition: AutoAdvanceCondition | null,
  ) {
    if (!editingStep) return;
    const step = editingStep;
    setEditingStep(null);
    try {
      await updateStep(step.id, { autoAdvanceCondition: condition });
    } catch (e) {
      toast.error(
        getApiErrorMessage(e, { fallback: "Couldn't save condition" }),
      );
    }
  }

  if (isLoading) {
    return (
      <div className="workflow-tab">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (error) {
    return <div className="admin-error">{error}</div>;
  }

  return (
    <div className="workflow-tab">
      <div className="workflow-tab__header">
        <h2>Pipeline steps</h2>
        <div className="workflow-tab__add" ref={addMenuRef}>
          <button
            type="button"
            className="btn"
            aria-haspopup="menu"
            aria-expanded={addMenuOpen}
            onClick={() => setAddMenuOpen((o) => !o)}
          >
            + Add step ▼
          </button>
          {addMenuOpen && (
            <div role="menu" className="workflow-tab__add-menu">
              {addable.length === 0 && (
                <span className="workflow-tab__add-empty">
                  All step types added
                </span>
              )}
              {addable.map((t) => (
                <button
                  key={t}
                  type="button"
                  role="menuitem"
                  onClick={() => void handleAdd(t)}
                >
                  {formatStep(t)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="workflow-linear-steps">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(e) => void handleDragEnd(e)}
        >
          <SortableContext
            items={linearSteps.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            {linearSteps.map((step) => (
              <StepRow
                key={step.id}
                step={step}
                onRemove={() => void handleRemove(step)}
                onRename={() => void handleRename(step)}
                onEditCondition={() => setEditingStep(step)}
                onChangeReverifyPolicy={(v) => void handleChangeReverifyPolicy(step, v)}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      {sideChannelSteps.length > 0 && (
        <section className="workflow-side-channel" aria-label="Side channels">
          <div className="workflow-side-channel__header">
            <span className="workflow-side-channel__title">Side channels</span>
            <span className="workflow-side-channel__hint">
              Documents are routed here on escalation — not passed through after Export.
            </span>
          </div>
          {sideChannelSteps.map((step) => (
            <SideChannelStepRow
              key={step.id}
              step={step}
              onRemove={() => void handleRemove(step)}
              onRename={() => void handleRename(step)}
              onEditCondition={() => setEditingStep(step)}
              onChangeReverifyPolicy={(v) => void handleChangeReverifyPolicy(step, v)}
            />
          ))}
        </section>
      )}

      {editingStep && (
        <RoutingConditionDrawer
          key={editingStep.id}
          isOpen
          stepLabel={editingStep.label}
          condition={editingStep.autoAdvanceCondition}
          fields={fields}
          onSave={(c) => void handleSaveCondition(c)}
          onClose={() => setEditingStep(null)}
        />
      )}
    </div>
  );
}
